import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, map, Observable, Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import {
  RoutingProfileModel,
  PagedCollection,
  VehicleTypeEnum,
  DefaultRoutingProfilesModel,
  VehicleDimensionsThesholdsModel,
} from '../_models/profilews/profilews';
import { AlertService } from '../auth/services/alert.service';
import { ProfileVehicleType } from '../_models/daws/trip';
import { UserService } from './user.service';
import { SubscriptionLevel } from '../auth/models/user';

@Injectable({
  providedIn: 'root',
})
export class RoutingProfileService {
  constructor(
    private http: HttpClient,
    private alertService: AlertService,
    private userService: UserService
  ) {}
  private profilesLoaded = new BehaviorSubject<boolean>(false);
  readonly profilesLoaded$ = this.profilesLoaded.asObservable();
  hasProfiles(): boolean {
    let routingProfiles = localStorage
      ? JSON.parse(localStorage.getItem('routingProfileStore'))
      : {};
    routingProfiles = routingProfiles?.profiles || null;
    return routingProfiles === null ? false : true;
  }

  setProfiles(profiles: RoutingProfileModel[]) {
    profiles = profiles || [];
    const user = this.userService.getUser();
    profiles =
      user.subscriptionLevel === SubscriptionLevel.PREMIUM
        ? profiles
        : profiles.filter((profile) => profile.name !== 'RV');

    if (localStorage) {
      localStorage.setItem('routingProfileStore', JSON.stringify({ profiles }));
    }
  }

  getProfiles(): RoutingProfileModel[] {
    let routingProfiles = localStorage
      ? JSON.parse(localStorage.getItem('routingProfileStore'))
      : {};
    routingProfiles = routingProfiles?.profiles || null;
    return routingProfiles;
  }

  getProfile(profileId: number): RoutingProfileModel {
    if (this.hasProfiles()) {
      const profiles = this.getProfiles();
      const profile: RoutingProfileModel = profiles.find((p) => {
        return p.routingProfileId === profileId && profileId !== -1; // All Default profile ids are negative values
      });
      return profile;
    }
  }
  getProfileId(profileName: string): number {
    let id: number;
    if (this.hasProfiles()) {
      const profiles = this.getProfiles();
      const profile = profiles.find((p) => {
        return p.name === profileName && profileName !== 'Not Using a Profile'; // All Default profile ids are negative values
      });
      id = profile?.routingProfileId || -1;
    }
    return id;
  }
  getAvailableProfileByVehicleType(vehicleType: ProfileVehicleType) {
    const profiles = this.getProfiles();
    switch (vehicleType) {
      case ProfileVehicleType.Auto:
        return profiles.find((x) => x.vehicleType === VehicleTypeEnum.Auto);
        break;
      case ProfileVehicleType.Bus:
        return profiles.find((x) => x.vehicleType === VehicleTypeEnum.Bus);
        break;
      case ProfileVehicleType.LightDuty:
        return profiles.find(
          (x) => x.vehicleType === VehicleTypeEnum.LightDuty
        );
        break;
      case ProfileVehicleType.MidsizeTruck:
        return profiles.find(
          (x) => x.vehicleType === VehicleTypeEnum.MidsizeTruck
        );
        break;
      case ProfileVehicleType.Truck:
        return profiles.find((x) => x.vehicleType === VehicleTypeEnum.Truck);
        break;
    }
  }
  // The service API has following options to use if the proflie list gets too long. May need to implement this later.
  // But how to display a paginated list is a dropdown?
  // pageOptions
  //  .Offset.Value, number
  //  .Limit.Value, number
  //  .SortExpression, string
  //  .SortDirection,  string
  // searchOptions
  //  .Query,  string
  // Sample: offset=0&limit=20&sortExpression=Name&sortDirection=ASC"
  retrieveCustomProfiles(): Observable<PagedCollection<RoutingProfileModel[]>> {
    const customProfileUrl = `${environment.profileUrl}?offset=0&limit=1000`;

    return this.http.get<PagedCollection<RoutingProfileModel[]>>(
      customProfileUrl
    );
  }
  retrieveDefaultProfiles(): Observable<
    PagedCollection<RoutingProfileModel[]>
  > {
    const defaultProfileUrl = `${environment.profileUrl}default`;

    return this.http.get<PagedCollection<RoutingProfileModel[]>>(
      defaultProfileUrl
    );
  }
  retrieveAllProfilesSubscrition(): Subscription {
    return forkJoin([
      this.retrieveDefaultProfiles(),
      this.retrieveCustomProfiles(),
    ]).subscribe(
      (
        allProfiles: [
          PagedCollection<RoutingProfileModel[]>,
          PagedCollection<RoutingProfileModel[]>
        ]
      ) => {
        if (allProfiles) {
          const profileClone: DefaultRoutingProfilesModel = JSON.parse(
            JSON.stringify(allProfiles[0].data)
          );
          localStorage.setItem(
            'routingProfileVehicleDim',
            JSON.stringify(profileClone.vehicleDimensionMinMax)
          );
        }
        const profiles = allProfiles
          .map((profileData: any) => {
            return profileData.data.profiles
              ? profileData.data.profiles
              : profileData.data;
          })
          .reduce((accumulator, value) => accumulator.concat(value), []);

        this.setProfiles(profiles);
        this.profilesLoaded.next(true);
      },
      (err) => {
        // TODO temporarily setting loaded to true, we change it back once the profile work is working
        this.profilesLoaded.next(true);
        this.setProfiles([]);
        // Showing error message at the bottom of the page.
        // Temporary solution to get feedback. // Todo-yz
        const errorMessage =
          'There was an error retrieving the routing profiles, please try again later. Make sure you have permissions to access the feature.'; // Todo-yz Temporary message.
        this.alertService.error(errorMessage, true, 'app-modal-alert', true);
      }
    );
  }
  getActiveDefaultProfileId(profiles: RoutingProfileModel[]): number {
    const customDefaultId = profiles.find(
      (profile) => profile?.isAccountDefault
    )?.routingProfileId;
    const defaultProfiles = profiles.find(
      (pf) =>
        pf.name === 'Heavy Articulated' ||
        pf.name === 'Heavy Duty Semitrailer (48\' x 96")'
    );
    if (defaultProfiles || customDefaultId) {
      return customDefaultId
        ? customDefaultId
        : defaultProfiles.routingProfileId;
    }
  }
  createCustomProfile(profileData: RoutingProfileModel): Observable<any> {
    const profileUrl = `${environment.routingProfileUrl}/routingprofiles`;
    return this.http.post<any>(profileUrl, profileData).pipe(
      map((res) => res),
      (error) => error
    );
  }

  getProfileDimensionsMinMax(): VehicleDimensionsThesholdsModel[] {
    return JSON.parse(localStorage.getItem('routingProfileVehicleDim'));
  }
}
